import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { createNotification } from '@/lib/notifications';
import { useAuth } from '@/hooks/useAuth';
import {
  DBFolder,
  DBFile,
  formatBytes,
  formatDate,
  getFileExt,
  isImageType,
} from './filesTypes';
import FileRow from './FileRow';
import FileUploader from '@/components/feature/FileUploader';
import { openProjectFileInNewTab, FILE_OPEN_ERROR_MESSAGE, useSignedFileUrls } from '@/lib/storageSignedUrl';
import { buildFileAudienceWritePayload, type FileAudience } from '@/lib/fileAudience';

interface FilesTabProps {
  projectId: string;
  canUpload?: boolean;
}

type VisibilityFilter = 'all' | 'visible' | 'hidden';

export default function FilesTab({ projectId, canUpload = true }: FilesTabProps) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [folders, setFolders] = useState<DBFolder[]>([]);
  const [files, setFiles] = useState<DBFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState<string | null>(null);
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>('visible');
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [pdfPreview, setPdfPreview] = useState<DBFile | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAddFolderModal, setShowAddFolderModal] = useState(false);
  const [uploadFolderId, setUploadFolderId] = useState<string>('');
  const [newFolderName, setNewFolderName] = useState('');
  const [actionFeedback, setActionFeedback] = useState<string>('');

  // Signed display URLs keyed by file id (falls back to file_url per record).
  const fileUrls = useSignedFileUrls(files);

  useEffect(() => {
    if (projectId) {
      loadFolders();
      loadFiles();
    }
  }, [projectId]);

  async function loadFolders() {
    const { data } = await supabase
      .from('jphph_project_folders')
      .select('*')
      .eq('project_id', projectId)
      .order('sort_order', { ascending: true });
    if (data) setFolders(data as DBFolder[]);
  }

  async function loadFiles() {
    setLoading(true);
    const { data, error } = await supabase
      .from('jphph_project_files')
      .select('*')
      .eq('project_id', projectId)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false });
    if (error) {
      console.error('Error loading files:', error);
    }
    if (data) setFiles(data as DBFile[]);
    setLoading(false);
  }

  async function handleDeleteFile(fileId: string, fileUrl: string) {
    const target = files.find((f) => f.id === fileId);

    // Soft delete in database
    const { error } = await supabase
      .from('jphph_project_files')
      .update({ is_deleted: true, deleted_at: new Date().toISOString() })
      .eq('id', fileId);

    if (error) {
      showFeedback(t('files.deleteFileFailed'));
      return;
    }

    // Try to remove from storage (best effort). Prefer the explicit storage
    // reference; fall back to parsing the legacy public file_url.
    try {
      if (target?.storage_bucket && target?.storage_path) {
        await supabase.storage.from(target.storage_bucket).remove([target.storage_path]);
      } else if (fileUrl && fileUrl.includes('/project-files/')) {
        const urlParts = fileUrl.split('/project-files/');
        if (urlParts.length > 1) {
          const path = urlParts[1];
          await supabase.storage.from('project-files').remove([path]);
        }
      }
    } catch {
      // Storage removal is best-effort; file is already soft-deleted
    }

    setFiles((prev) => prev.filter((f) => f.id !== fileId));
    showFeedback(t('files.fileDeleted'));
  }

  async function handleToggleVisibility(fileId: string, currentlyVisible: boolean) {
    const newVisible = !currentlyVisible;
    const { error } = await supabase
      .from('jphph_project_files')
      .update({ is_visible: newVisible })
      .eq('id', fileId);

    if (error) {
      showFeedback(t('files.updateVisibilityFailed'));
      return;
    }

    setFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, is_visible: newVisible } : f))
    );
    showFeedback(newVisible ? t('files.fileNowVisible') : t('files.fileHidden'));
  }

  async function handleChangeAudience(fileId: string, audience: FileAudience) {
    const current = files.find((file) => file.id === fileId);
    if (!current) return;
    const payload = buildFileAudienceWritePayload(audience, { existingPhotoType: current.photo_type });
    const { error } = await supabase
      .from('jphph_project_files')
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq('id', fileId);
    if (error) {
      showFeedback(error.message || 'Failed to update file audience.');
      return;
    }
    setFiles((prev) => prev.map((file) => file.id === fileId ? { ...file, ...payload } : file));
    showFeedback('File audience updated.');
  }

  async function handleMoveFolder(fileId: string, folderId: string | null) {
    const { error } = await supabase
      .from('jphph_project_files')
      .update({ folder_id: folderId })
      .eq('id', fileId);

    if (error) {
      showFeedback(t('files.moveFileFailed'));
      return;
    }

    setFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, folder_id: folderId } : f))
    );

    const folderName = folderId
      ? folders.find((f) => f.id === folderId)?.name
      : null;
    showFeedback(folderName ? t('files.movedToFolder', { folder: folderName }) : t('files.removedFromFolder'));
  }

  function showFeedback(msg: string) {
    setActionFeedback(msg);
    setTimeout(() => setActionFeedback(''), 3000);
  }

  async function handleAddFolder() {
    if (!newFolderName.trim() || !user) return;
    const { data } = await supabase
      .from('jphph_project_folders')
      .insert({
        project_id: projectId,
        name: newFolderName.trim(),
        icon: 'ri-folder-line',
        color: '#E8621A',
        sort_order: folders.length,
        created_by: user.id,
      })
      .select()
      .maybeSingle();
    if (data) setFolders((prev) => [...prev, data as DBFolder]);
    setNewFolderName('');
    setShowAddFolderModal(false);
  }

  // Apply folder + visibility filters
  let displayFiles = activeFolder
    ? files.filter((f) => f.folder_id === activeFolder)
    : files;

  if (visibilityFilter === 'visible') {
    displayFiles = displayFiles.filter((f) => f.is_visible !== false);
  } else if (visibilityFilter === 'hidden') {
    displayFiles = displayFiles.filter((f) => f.is_visible === false);
  }

  const activeFolderObj = folders.find((f) => f.id === activeFolder);

  const folderFileCounts = folders.reduce<Record<string, number>>((acc, folder) => {
    acc[folder.id] = files.filter(
      (f) => f.folder_id === folder.id && f.is_deleted !== true
    ).length;
    return acc;
  }, {});

  const handleOpenImage = (url: string) => setLightboxUrl(url);
  const handleOpenPdf = (file: DBFile) => setPdfPreview(file);
  const handleOpenOther = (file: DBFile) => {
    void (async () => {
      const result = await openProjectFileInNewTab(file);
      if (!result.ok) {
        setActionFeedback(result.error || FILE_OPEN_ERROR_MESSAGE);
      }
    })();
  };

  // Resolved (signed) URL for the open PDF preview, falling back to file_url.
  const pdfPreviewUrl = pdfPreview ? fileUrls[pdfPreview.id] || pdfPreview.file_url : '';

  const recentFiles = activeFolder ? displayFiles : displayFiles.slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 text-[#E8621A]">
            <i className="ri-loader-4-line text-3xl animate-spin"></i>
          </div>
          <p className="text-[#9CA3AF] text-sm">{t('files.loadingFiles')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-full overflow-x-hidden relative">
      {/* Feedback toast */}
      {actionFeedback && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] bg-[#1A1A1A] text-white text-sm font-medium px-5 py-3 rounded-xl shadow-sm whitespace-nowrap">
          {actionFeedback}
        </div>
      )}

      {/* Folder grid view */}
      {!activeFolder && (
        <>
          <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
            <h3 className="font-bold text-[#1A1A1A] text-sm">{t('files.title')}</h3>
            <div className="flex items-center gap-2">
              {canUpload && (
                <button
                  onClick={() => setShowAddFolderModal(true)}
                  className="flex items-center gap-1.5 bg-white border border-gray-200 text-[#374151] px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-folder-add-line"></i>
                  {t('files.folder')}
                </button>
              )}
              {canUpload && (
                <button
                  onClick={() => {
                    setUploadFolderId('');
                    setShowUploadModal(true);
                  }}
                  className="flex items-center gap-1.5 bg-[#E8621A] text-white px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-upload-2-line"></i>
                  {t('files.upload')}
                </button>
              )}
            </div>
          </div>

          {folders.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => setActiveFolder(folder.id)}
                  className="bg-white rounded-2xl border border-gray-100 p-4 text-left cursor-pointer hover:border-gray-200 transition-colors"
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${folder.color}15` }}
                  >
                    <i
                      className={`${folder.icon} text-xl`}
                      style={{ color: folder.color }}
                    ></i>
                  </div>
                  <div className="text-[#1A1A1A] text-sm font-semibold">
                    {folder.name}
                  </div>
                  <div className="text-[#9CA3AF] text-xs mt-0.5">
                    {folderFileCounts[folder.id] || 0} {t('files.allFiles').toLowerCase()}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* All files / recent */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between flex-wrap gap-2">
              <h4 className="text-[#1A1A1A] text-sm font-bold">
                {folders.length > 0 ? t('files.recentFiles') : t('files.allFiles')}
              </h4>
              <span className="text-[#9CA3AF] text-xs">
                {displayFiles.length} {t('files.total')}
              </span>
            </div>

            {/* Visibility filter */}
            {canUpload && (
              <div className="px-4 py-2 border-b border-gray-50 flex items-center gap-1 overflow-x-auto">
                {(['all', 'visible', 'hidden'] as VisibilityFilter[]).map(
                  (f) => (
                    <button
                      key={f}
                      onClick={() => setVisibilityFilter(f)}
                      className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${
                        visibilityFilter === f
                          ? 'bg-[#E8621A] text-white'
                          : 'bg-gray-100 text-[#9CA3AF] hover:bg-gray-200'
                      }`}
                    >
                      {f === 'all' ? t('files.all') : f === 'visible' ? t('files.visible') : t('files.hidden')}
                    </button>
                  )
                )}
              </div>
            )}

            {recentFiles.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
                  <i className="ri-folder-open-line text-4xl"></i>
                </div>
                <div className="text-[#374151] font-semibold text-sm mb-1">
                  {t('files.noFiles')}
                </div>
                <div className="text-[#9CA3AF] text-xs">
                  {t('files.uploadToGetStarted')}
                </div>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {recentFiles.map((file) => (
                  <FileRow
                    key={file.id}
                    file={file}
                    displayUrl={fileUrls[file.id] || file.file_url}
                    canUpload={canUpload}
                    folders={folders}
                    onOpenImage={handleOpenImage}
                    onOpenPdf={handleOpenPdf}
                    onOpenOther={handleOpenOther}
                    onDelete={(id) =>
                      handleDeleteFile(
                        id,
                        files.find((f) => f.id === id)?.file_url || ''
                      )
                    }
                    onToggleVisibility={handleToggleVisibility}
                    onMoveFolder={handleMoveFolder}
                    onChangeAudience={handleChangeAudience}
                    onChangeAudience={handleChangeAudience}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Folder detail view */}
      {activeFolder && (
        <>
          <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setActiveFolder(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-[#374151] flex-shrink-0"
              >
                <i className="ri-arrow-left-line text-base"></i>
              </button>
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: `${activeFolderObj?.color}15`,
                }}
              >
                <i
                  className={`${activeFolderObj?.icon} text-sm`}
                  style={{ color: activeFolderObj?.color }}
                ></i>
              </div>
              <h3 className="font-bold text-[#1A1A1A] text-sm truncate">
                {activeFolderObj?.name}
              </h3>
              <span className="text-[#9CA3AF] text-xs flex-shrink-0">
                ({displayFiles.length})
              </span>
            </div>
            {canUpload && (
              <button
                onClick={() => {
                  setUploadFolderId(activeFolder);
                  setShowUploadModal(true);
                }}
                className="flex items-center gap-1.5 bg-[#E8621A] text-white px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                <i className="ri-upload-2-line"></i>
                {t('files.upload')}
              </button>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {/* Visibility filter */}
            {canUpload && (
              <div className="px-4 py-2 border-b border-gray-50 flex items-center gap-1 overflow-x-auto">
                {(['all', 'visible', 'hidden'] as VisibilityFilter[]).map(
                  (f) => (
                    <button
                      key={f}
                      onClick={() => setVisibilityFilter(f)}
                      className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${
                        visibilityFilter === f
                          ? 'bg-[#E8621A] text-white'
                          : 'bg-gray-100 text-[#9CA3AF] hover:bg-gray-200'
                      }`}
                    >
                      {f === 'all' ? t('files.all') : f === 'visible' ? t('files.visible') : t('files.hidden')}
                    </button>
                  )
                )}
              </div>
            )}

            {displayFiles.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
                  <i className="ri-folder-open-line text-4xl"></i>
                </div>
                <div className="text-[#374151] font-semibold text-sm mb-1">
                  {t('files.noFiles')}
                </div>
                <div className="text-[#9CA3AF] text-xs">
                  {t('files.uploadToFolder')}
                </div>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {displayFiles.map((file) => (
                  <FileRow
                    key={file.id}
                    file={file}
                    displayUrl={fileUrls[file.id] || file.file_url}
                    canUpload={canUpload}
                    folders={folders}
                    onOpenImage={handleOpenImage}
                    onOpenPdf={handleOpenPdf}
                    onOpenOther={handleOpenOther}
                    onDelete={(id) =>
                      handleDeleteFile(
                        id,
                        files.find((f) => f.id === id)?.file_url || ''
                      )
                    }
                    onToggleVisibility={handleToggleVisibility}
                    onMoveFolder={handleMoveFolder}
                    onChangeAudience={handleChangeAudience}
                    onChangeAudience={handleChangeAudience}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Add Folder Modal */}
      {showAddFolderModal && (
        <div className="fixed inset-0 z-[60] flex items-end lg:items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowAddFolderModal(false)}
          />
          <div className="relative bg-white w-full lg:max-w-sm rounded-t-3xl lg:rounded-3xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-[#1A1A1A] font-bold text-base">{t('files.newFolder')}</h3>
              <button
                onClick={() => setShowAddFolderModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-[#374151]"></i>
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-[#555555] text-xs font-semibold mb-1.5">
                  {t('files.folderName')}
                </label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder={t('files.folderNamePlaceholder')}
                  className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]"
                  autoFocus
                />
              </div>
              <button
                onClick={handleAddFolder}
                disabled={!newFolderName.trim()}
                className="w-full py-3.5 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {t('files.createFolder')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && canUpload && user && (
        <FileUploader
          projectId={projectId}
          currentUserId={user.id}
          open={showUploadModal}
          onClose={() => {
            setShowUploadModal(false);
            setUploadFolderId('');
          }}
          onUploadComplete={async () => {
            await loadFiles();
            await createNotification({
              project_id: projectId,
              notification_type: 'file',
              title: t('files.newFileUploaded'),
              body: 'A new file has been added to the project files.',
              target_role: 'management',
              link_path: `/management/projects/${projectId}`,
              created_by: user.id,
            });
          }}
          accountType="management"
          category="mixed"
          allowMultiple={true}
          allowCamera={false}
          allowPdf={true}
          enablePerFileCaption={true}
          showVisibilitySelector={true}
          visibilityScope="all"
          title={t('files.uploadFiles')}
          folderId={uploadFolderId || null}
        />
      )}

      {/* Image Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black/95 z-[70] flex flex-col"
          onClick={() => setLightboxUrl(null)}
        >
          <div className="flex items-center justify-end px-4 py-4 flex-shrink-0">
            <button
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white cursor-pointer"
              onClick={() => setLightboxUrl(null)}
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <div
            className="flex-1 flex items-center justify-center px-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxUrl}
              alt="Preview"
              className="max-w-full max-h-full rounded-2xl object-contain"
            />
          </div>
          <div className="py-4"></div>
        </div>
      )}

      {/* PDF Preview */}
      {pdfPreview && (
        <div className="fixed inset-0 bg-black/90 z-[70] flex flex-col">
          <div className="flex items-center justify-between px-4 py-4 flex-shrink-0 bg-[#1A1A1A]">
            <div className="min-w-0">
              <p className="text-white font-semibold text-sm truncate">
                {pdfPreview.file_name}
              </p>
              <p className="text-white/50 text-xs">
                {formatBytes(pdfPreview.file_size)}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  if (!pdfPreview) return;
                  void (async () => {
                    const result = await openProjectFileInNewTab(pdfPreview);
                    if (!result.ok) {
                      setActionFeedback(result.error || FILE_OPEN_ERROR_MESSAGE);
                    }
                  })();
                }}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white cursor-pointer"
              >
                <i className="ri-external-link-line text-base"></i>
              </button>
              <button
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white cursor-pointer"
                onClick={() => setPdfPreview(null)}
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center px-6">
            <div className="bg-white rounded-2xl p-8 text-center max-w-sm w-full">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-[#FEF0E7] rounded-2xl">
                <i className="ri-file-pdf-line text-[#E8621A] text-3xl"></i>
              </div>
              <p className="text-[#1A1A1A] font-bold text-sm mb-1">
                {pdfPreview.file_name}
              </p>
              <p className="text-[#9CA3AF] text-xs mb-4">
                {formatBytes(pdfPreview.file_size)} ·{' '}
                {(pdfPreview.file_type || 'file').toUpperCase()}
              </p>
              {pdfPreview.note && (
                <p className="text-[#6B7280] text-xs mb-4 bg-[#F9FAFB] rounded-xl px-3 py-2">
                  {pdfPreview.note}
                </p>
              )}
              <button
                type="button"
                onClick={() => {
                  if (!pdfPreview) return;
                  void (async () => {
                    const result = await openProjectFileInNewTab(pdfPreview);
                    if (!result.ok) {
                      setActionFeedback(result.error || FILE_OPEN_ERROR_MESSAGE);
                    }
                  })();
                }}
                className="block w-full py-3 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap text-center"
              >
                <i className="ri-external-link-line mr-2"></i>
                {t('files.openFile')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}